import type { CSSProperties, ReactNode } from 'react';
import type { AxisTick, Deployment, TimelineBar } from '@/state/experiments/timeline';
import { useEffect, useRef, useState } from 'react';
import { useLocalStorageState } from 'hooks/useLocalStorageState';
import type { LibraryRow } from '@/state/experiments/library-learnings';
import { AlertTriangle } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ALL_TIME, TIMELINE_RANGES, useExperimentTimeline } from '@/state/experiments/useExperimentTimeline';
import { axisTicks, trackWidth } from '@/state/experiments/timeline';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';

type TimelineViewProps = { allRows: readonly LibraryRow[]; visibleRows: readonly LibraryRow[] };

type TimelineManager = ReturnType<typeof useExperimentTimeline>;

/**
 * Status colour, the same semantics as the library's status chips: running is live (ok),
 * deployed is the shipped outcome (accent), ended is neutral, draft is still a selection.
 * Colour says *what* the test is; the line style says how sure the dates are.
 */
const FILL: Record<string, string> = {
  Running: 'bg-ok',
  Deployed: 'bg-accent',
  Ended: 'bg-bar-muted',
  Draft: 'bg-sel',
  QA: 'bg-sel',
};

const LINE: Record<string, string> = {
  Running: 'border-ok',
  Deployed: 'border-accent',
  Ended: 'border-bar-muted',
  Draft: 'border-sel',
  QA: 'border-sel',
};

/** Spelled out rather than derived from FILL: Tailwind only generates classes it can read in the source. */
const ESTIMATED: Record<string, string> = {
  Running: 'bg-ok/35',
  Deployed: 'bg-accent/35',
  Ended: 'bg-bar-muted/35',
  Draft: 'bg-sel/35',
  QA: 'bg-sel/35',
};

const FADE: Record<string, string> = {
  Running: 'from-ok',
  Deployed: 'from-accent',
  Ended: 'from-bar-muted',
  Draft: 'from-sel',
  QA: 'from-sel',
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** The pinned name column's default: the table's 14rem, narrower on a phone so the track keeps some room. */
const defaultNameColumnPx = (viewportPx: number) => (viewportPx < 640 ? 150 : 224);

const MIN_NAME_COLUMN_PX = 140;
/** Never more than 60% of the panel, so a wide name column cannot push the track off screen. */
const maxNameColumnPx = (viewportPx: number) => Math.max(MIN_NAME_COLUMN_PX, Math.min(640, viewportPx * 0.6));
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Per viewer and per browser: a layout preference, not something anyone else needs to see. */
const NAME_WIDTH_KEY = 'PERTENTO_TIMELINE_NAME_WIDTH';

/** Where today sits once scrolled: two thirds of the way across, so what is live and what is next are in view. */
const TODAY_AT = 2 / 3;

const shortDate = (epoch: number) =>
  new Date(epoch).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });

export const TimelineView = ({ allRows, visibleRows }: TimelineViewProps) => {
  const manager = useExperimentTimeline({ allRows, visibleRows });
  const { groups, unplaced, from, to } = manager;
  const pos = (t: number) => Math.min(100, Math.max(0, ((t - from) / (to - from)) * 100));

  // Measured, because the track is sized by time and has to know how much of it fits.
  // The panel is observed, not the scroll container: the container is not rendered while
  // loading, so an observer attached to it on first open found nothing and never retried,
  // which left the width at 0 (name column stuck, no scroll to today) until a reload.
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [viewportPx, setViewportPx] = useState(0);
  const showing = groups.length > 0 && !manager.isLoading;

  useEffect(() => {
    const element = panelRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setViewportPx(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Resizable, because experiment names are long and 14rem truncates most of them.
  const [savedNameCol, setSavedNameCol] = useLocalStorageState<number | null>(NAME_WIDTH_KEY, null);
  const nameCol = clamp(
    savedNameCol ?? defaultNameColumnPx(viewportPx),
    MIN_NAME_COLUMN_PX,
    maxNameColumnPx(viewportPx),
  );
  const trackPx = trackWidth({ from, to, months: manager.range, viewportPx: Math.max(0, viewportPx - nameCol) });
  // The name column's width lives in a CSS variable, so a drag can move it without
  // re-rendering every row on every pixel; the state is only committed on release.
  const columns = `var(--name-col) ${trackPx}px`;
  const trackStyle = {
    '--name-col': `${nameCol}px`,
    width: viewportPx ? `calc(var(--name-col) + ${trackPx}px)` : '100%',
  } as CSSProperties;
  const clampName = (width: number) => clamp(width, MIN_NAME_COLUMN_PX, maxNameColumnPx(viewportPx));
  const ticks = axisTicks({ from, to, trackPx });

  // Opened, and on every range change, scrolled so today is two thirds across the
  // visible track: history is a scroll to the left, not the first thing on screen.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || viewportPx === 0) return;
    const visibleTrack = viewportPx - nameCol;
    element.scrollLeft = Math.max(0, (pos(manager.nowMs) / 100) * trackPx - visibleTrack * TODAY_AT);
    // Not on trackPx: resizing the name column changes it, and re-scrolling to today on
    // every pixel of a drag would yank the view out from under the pointer.
  }, [manager.range, showing, viewportPx > 0]);

  return (
    <TooltipProvider>
      <div ref={panelRef} className="bg-panel border-line rounded-card overflow-hidden border">
        <TimelineControls manager={manager} />

        {manager.isLoading ? (
          <p className="supporting px-4 py-10">Loading the timeline…</p>
        ) : groups.length === 0 ? (
          <p className="supporting px-4 py-10">
            {emptyMessage({ visible: visibleRows.length, hiddenByWindow: manager.hiddenByWindow, from, to })}
          </p>
        ) : (
          <div ref={scrollRef} className="overflow-x-auto">
            <div ref={trackRef} style={trackStyle}>
              <Axis
                ticks={ticks}
                nowMs={manager.nowMs}
                pos={pos}
                columns={columns}
                nameCol={nameCol}
                minWidth={MIN_NAME_COLUMN_PX}
                maxWidth={maxNameColumnPx(viewportPx)}
                onPreview={(width) => trackRef.current?.style.setProperty('--name-col', `${clampName(width)}px`)}
                onResize={(width) => setSavedNameCol(clampName(width))}
                onResetWidth={() => setSavedNameCol(null)}
              />
              {groups.map((group) => (
                <section key={group.key}>
                  {/* The label is pinned like the names, so a group stays named while scrolled. */}
                  <h3 className="micro-label border-line text-foreground border-b py-2.5">
                    <span className="bg-panel sticky left-0 inline-block px-4">{group.label}</span>
                  </h3>
                  {group.bars.map((bar) => (
                    <BarRow
                      key={bar.id}
                      bar={bar}
                      pos={pos}
                      columns={columns}
                      nowMs={manager.nowMs}
                      interaction={manager.interactions.get(bar.id) ?? null}
                    />
                  ))}
                </section>
              ))}
            </div>
          </div>
        )}

        {!manager.isLoading && groups.length > 0 && manager.hiddenByWindow > 0 && (
          <p className="supporting border-line border-t px-4 py-2">
            {manager.hiddenByWindow} more outside {shortDate(from)} to {shortDate(to)}.
          </p>
        )}

        {!manager.isLoading && unplaced.length > 0 && (
          <details className="border-line border-t px-4 py-3">
            <summary className="supporting cursor-pointer">
              {unplaced.length} experiment{unplaced.length === 1 ? '' : 's'} could not be placed
            </summary>
            <ul className="mt-2 grid gap-1">
              {unplaced.map((item) => (
                <li key={item.id} className="text-meta">
                  <NavLink to={`/experiments/${item.id}`} className="text-foreground hover:underline">
                    {item.name}
                  </NavLink>
                  <span className="text-dim">: {item.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </TooltipProvider>
  );
};

type TimelineControlsProps = { manager: TimelineManager };

const TimelineControls = ({ manager }: TimelineControlsProps) => (
  <div className="border-line flex flex-col gap-2 border-b px-4 py-2 md:flex-row md:items-center md:gap-4">
    <div className="flex items-center gap-1.5" data-tour="experiments-timeline-range">
      {TIMELINE_RANGES.map((months) => (
        <button
          key={months}
          type="button"
          aria-pressed={manager.range === months}
          onClick={() => manager.setRange(months)}
          className={cn(
            'micro-label rounded-pill flex min-h-9 items-center border px-2.5 py-1 md:min-h-0',
            FOCUS_RING,
            manager.range === months
              ? 'bg-sel-bg border-sel-line text-foreground'
              : 'border-input text-dim hover:bg-hover hover:text-foreground',
          )}
        >
          {months === ALL_TIME ? 'All time' : `${months} months`}
        </button>
      ))}
    </div>

    {/* The key to both encodings: colour is the status, the line style is how sure the
        date is. The deployed mark is kept whatever the status is now, so a winner that
        shipped and was later ended still reads as shipped. Each bar's aria-label and its
        date line say the same in words. */}
    <div
      className="text-dim flex flex-wrap items-center gap-x-4 gap-y-1 text-meta md:ml-auto"
      data-tour="experiments-timeline-legend"
    >
      {(['Running', 'Deployed', 'Ended', 'Draft'] as const).map((status) => (
        <LegendItem key={status} swatch={<span className={cn('size-2 rounded-full', FILL[status])} />}>
          {status}
        </LegendItem>
      ))}
      <LegendItem swatch={<span className="bg-dim h-2 w-5 rounded-[4px]" />}>Happened</LegendItem>
      <LegendItem swatch={<span className="border-dim h-2 w-5 rounded-[4px] border-2 border-dashed" />}>
        Scheduled
      </LegendItem>
      <LegendItem swatch={<span className="bg-dim/35 border-dim/60 h-2 w-5 rounded-[4px] border border-dashed" />}>
        Estimated
      </LegendItem>
      <LegendItem
        swatch={
          <span className="relative flex w-5 items-center">
            <span className="bg-accent h-0.5 w-full" />
            <DeployDiamond />
          </span>
        }
      >
        Deployment
      </LegendItem>
      <LegendItem swatch={<AlertTriangle className="text-warn size-3.5" />}>Interacts with another test</LegendItem>
    </div>
  </div>
);

type LegendItemProps = { swatch: ReactNode; children: ReactNode };

const LegendItem = ({ swatch, children }: LegendItemProps) => (
  <span className="flex items-center gap-1.5">
    {swatch}
    {children}
  </span>
);

type AxisProps = {
  ticks: readonly AxisTick[];
  nowMs: number;
  pos: (t: number) => number;
  columns: string;
  nameCol: number;
  minWidth: number;
  maxWidth: number;
  onPreview: (width: number) => void;
  onResize: (width: number) => void;
  onResetWidth: () => void;
};

/** Month starts as ticks, and today as the one line that is not recessive. */
const Axis = ({
  ticks,
  nowMs,
  pos,
  columns,
  nameCol,
  minWidth,
  maxWidth,
  onPreview,
  onResize,
  onResetWidth,
}: AxisProps) => {
  return (
    // The table header's band: 48px, panel2, a hairline under it, so the two layouts
    // read as one surface when toggling between them.
    <div className="border-line bg-panel2 grid border-b" style={{ gridTemplateColumns: columns }}>
      <div className="micro-label bg-panel2 border-line sticky left-0 z-10 flex h-12 items-center border-r px-4">
        Experiment
        <ColumnResizer
          width={nameCol}
          minWidth={minWidth}
          maxWidth={maxWidth}
          onPreview={onPreview}
          onResize={onResize}
          onReset={onResetWidth}
        />
      </div>
      {/* Two rows: months above, today below. On one line "Today" ran into whichever
          month began within a few days of it ("TODAYOCT" at the end of September). */}
      <div className="relative h-12">
        {ticks.map((tick) => (
          <span
            key={tick.at}
            className="micro-label text-dim absolute top-2 -translate-x-1/2 whitespace-nowrap"
            style={{ left: `${pos(tick.at)}%` }}
          >
            {tick.label}
          </span>
        ))}
        <span
          className="micro-label border-accent-line bg-panel text-accent rounded-pill absolute top-6 -translate-x-1/2 border px-1.5 leading-4"
          style={{ left: `${pos(nowMs)}%` }}
        >
          Today
        </span>
      </div>
    </div>
  );
};

type ColumnResizerProps = {
  width: number;
  minWidth: number;
  maxWidth: number;
  /** Called on every move: moves the column without a React render. */
  onPreview: (width: number) => void;
  /** Called once on release, and on each key press: commits the width. */
  onResize: (width: number) => void;
  onReset: () => void;
};

const KEYBOARD_STEP_PX = 16;

/**
 * The drag handle on the name column's edge. Pointer capture keeps the drag alive when
 * the pointer leaves the handle, which it does on the first fast movement. A lost capture
 * (an OS gesture, a window switch) ends the drag, so the handle never keeps resizing with
 * no button held. Arrow keys resize it too; Home or a double-click resets it.
 */
const ColumnResizer = ({ width, minWidth, maxWidth, onPreview, onResize, onReset }: ColumnResizerProps) => {
  const drag = useRef<{ startX: number; startWidth: number; latest: number } | null>(null);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the experiment column"
      aria-valuenow={Math.round(width)}
      aria-valuemin={minWidth}
      aria-valuemax={Math.round(maxWidth)}
      tabIndex={0}
      title="Drag to resize, double-click to reset"
      onPointerDown={(ev) => {
        if (ev.button !== 0) return;
        ev.preventDefault();
        ev.currentTarget.setPointerCapture(ev.pointerId);
        drag.current = { startX: ev.clientX, startWidth: width, latest: width };
      }}
      onPointerMove={(ev) => {
        if (!drag.current) return;
        drag.current.latest = drag.current.startWidth + ev.clientX - drag.current.startX;
        onPreview(drag.current.latest);
      }}
      onPointerUp={(ev) => {
        if (drag.current) onResize(drag.current.latest);
        ev.currentTarget.releasePointerCapture(ev.pointerId);
      }}
      onLostPointerCapture={() => {
        drag.current = null;
      }}
      onDoubleClick={onReset}
      onKeyDown={(ev) => {
        // preventDefault, or each press also scrolls the timeline sideways.
        if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight' || ev.key === 'Home') ev.preventDefault();
        if (ev.key === 'ArrowLeft') onResize(width - KEYBOARD_STEP_PX);
        if (ev.key === 'ArrowRight') onResize(width + KEYBOARD_STEP_PX);
        if (ev.key === 'Home') onReset();
      }}
      className={cn(
        // 24px wide, centred on the edge: an 8px handle could not be found on touch.
        'absolute inset-y-0 -right-3 z-20 w-6 cursor-col-resize touch-none',
        'after:bg-accent after:absolute after:inset-y-2 after:left-1/2 after:w-px after:-translate-x-1/2',
        'after:opacity-0 after:transition-opacity hover:after:opacity-100 focus-visible:after:opacity-100',
        FOCUS_RING,
      )}
    />
  );
};

type BarRowProps = {
  bar: TimelineBar;
  pos: (t: number) => number;
  /** grid-template-columns shared with the axis, so every row lines up with the ticks. */
  columns: string;
  nowMs: number;
  interaction: string | null;
};

const BarRow = ({ bar, pos, columns, nowMs, interaction }: BarRowProps) => {
  const fill = FILL[bar.status] ?? 'bg-dim';
  const line = LINE[bar.status] ?? 'border-dim';

  // Split at today: what already happened is solid, what is ahead is drawn by how it
  // is known. An open end runs to the edge and fades, rather than stopping at a date
  // nobody has.
  const pastEnd = Math.min(bar.end, nowMs);
  const hasPast = bar.startKind === 'actual' && pastEnd > bar.start;
  const futureStart = Math.max(bar.start, nowMs);
  const futureEnd = bar.endKind === 'open' ? Number.POSITIVE_INFINITY : bar.end;
  const hasFuture = futureEnd > futureStart;
  const futureKind = bar.endKind === 'open' ? 'open' : bar.startKind === 'estimated' ? 'estimated' : bar.endKind;

  const days = Math.max(1, Math.round(((bar.endKind === 'open' ? nowMs : bar.end) - bar.start) / DAY_MS));

  return (
    // Hover on both cells, opaque, rather than on the row: the pinned name cell needs a
    // solid background for bars to scroll under, so a translucent row hover showed only
    // on the track half.
    <div className="border-line group grid border-b last:border-b-0" style={{ gridTemplateColumns: columns }}>
      <div className="bg-panel group-hover:bg-panel2 border-line sticky left-0 z-10 flex min-w-0 flex-col justify-center gap-0.5 border-r p-4 transition-colors">
        {/* The id first, as in the table's ID column: it is how people refer to a test. */}
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="num text-dim shrink-0">{bar.id}</span>
          <NavLink
            to={`/experiments/${bar.id}`}
            title={bar.name}
            className="text-foreground truncate text-body font-medium hover:underline"
          >
            {bar.name}
          </NavLink>
        </div>
        {/* The dates, inline: the tooltip cannot be opened by a tap, so on a phone this
            line is the only way to read when a test ran. */}
        <span className="text-dim truncate text-nano">
          {rangeLabel(bar)}
          {bar.deployment && ` · deployed ${shortDate(bar.deployment.at)}`}
          {bar.stage && ` · ${bar.stage}`}
        </span>
      </div>

      <div className="group-hover:bg-panel2 relative transition-colors">
        {/* Today, repeated per row so it reads as one line down the chart. */}
        <span className="bg-accent-line absolute inset-y-0 w-px" style={{ left: `${pos(nowMs)}%` }} />

        <Tooltip>
          <TooltipTrigger asChild>
            {/* The hit target is the whole span plus padding, taller than the 8px mark. */}
            <button
              type="button"
              // min-w-2: a test that ended the day it started still gets a mark to hover.
              className={cn('absolute inset-y-0 flex min-w-2 items-center', FOCUS_RING)}
              style={{
                left: `${pos(bar.start)}%`,
                right: `${100 - pos(bar.endKind === 'open' ? Infinity : bar.end)}%`,
              }}
              aria-label={`${bar.name}, ${bar.status}: ${describe(bar)}${bar.deployment ? `. ${describeDeployment(bar.deployment)}` : ''}${interaction ? `. Interacts with ${interaction}` : ''}`}
            >
              <span className="relative flex h-2 w-full items-center">
                {!hasPast && !hasFuture && <span className={cn('absolute inset-0 rounded-[4px]', fill)} />}
                {hasPast && (
                  <span
                    className={cn('absolute inset-y-0 left-0 rounded-l-[4px]', fill, !hasFuture && 'rounded-r-[4px]')}
                    style={{ width: `${segmentWidth(bar, bar.start, pastEnd, pos)}%` }}
                  />
                )}
                {hasFuture && (
                  <span
                    className={cn(
                      'absolute inset-y-0 right-0 border-dashed',
                      !hasPast && 'rounded-l-[4px]',
                      futureKind === 'open' ? 'rounded-none' : 'rounded-r-[4px]',
                      futureKind === 'scheduled' && cn('border-2', line),
                      futureKind === 'estimated' && cn('border', line, ESTIMATED[bar.status] ?? 'bg-dim/35'),
                      futureKind === 'open' && cn('bg-gradient-to-r to-transparent', FADE[bar.status] ?? 'from-dim'),
                    )}
                    style={{ left: hasPast ? `${segmentWidth(bar, bar.start, futureStart, pos)}%` : 0 }}
                  />
                )}
                {interaction && (
                  <AlertTriangle
                    className={cn(
                      'text-warn bg-panel absolute -top-1 size-3.5 rounded-full',
                      // Inside the bar when it is clipped at the left edge, or it would sit
                      // under the pinned name column.
                      pos(bar.start) <= 0 ? 'left-1' : '-left-4',
                    )}
                  />
                )}
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">
            <p className="text-foreground font-medium">{bar.name}</p>
            <p className="text-dim">
              {bar.status} · {describe(bar)} · {days} days
            </p>
            {bar.note && <p className="text-dim">{bar.note}</p>}
            {bar.deployment && <p className="text-accent">{describeDeployment(bar.deployment)}.</p>}
            {interaction && <p className="text-warn">Interacts with {interaction}.</p>}
          </TooltipContent>
        </Tooltip>

        {/* After the bar, so the diamond sits on top of the test's last pixel: a test
            usually stops being a test at the moment it is deployed. */}
        {bar.deployment && <DeploymentMark name={bar.name} deployment={bar.deployment} pos={pos} />}
      </div>
    </div>
  );
};

/** The milestone: a small diamond centred on the deploy date. */
const DeployDiamond = () => (
  <span className="bg-accent border-panel absolute left-0 size-2.5 -translate-x-1/2 rotate-45 rounded-[2px] border-2" />
);

type DeploymentMarkProps = { name: string; deployment: Deployment; pos: (t: number) => number };

/**
 * The rollout after the test: a diamond at the first deploy, then a thin line for as long
 * as it stayed deployed. Still deployed runs solid to today and fades to the edge, as an
 * open-ended test does. Hidden from assistive tech, because the bar's own label already
 * says it; the tooltip is for the pointer.
 */
const DeploymentMark = ({ name, deployment, pos }: DeploymentMarkProps) => {
  const left = pos(deployment.at);
  const right = pos(deployment.ongoing ? Infinity : deployment.until);
  const span = right - left;
  const solid = span > 0 ? ((pos(deployment.until) - left) / span) * 100 : 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-hidden
          className="absolute inset-y-0 flex items-center"
          style={{ left: `${left}%`, width: `${span}%` }}
        >
          <span className="bg-accent absolute h-0.5" style={{ left: 0, width: `${solid}%` }} />
          {deployment.ongoing && (
            <span
              className="from-accent absolute right-0 h-0.5 bg-gradient-to-r to-transparent"
              style={{ left: `${solid}%` }}
            />
          )}
          <DeployDiamond />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-72">
        <p className="text-foreground font-medium">{name}</p>
        <p className="text-dim">{describeDeployment(deployment)}</p>
      </TooltipContent>
    </Tooltip>
  );
};

const describeDeployment = ({ at, until, ongoing }: Deployment) => {
  if (ongoing) return `Deployed ${shortDate(at)}, still deployed`;
  if (until <= at) return `Deployed ${shortDate(at)}`;
  const days = Math.max(1, Math.round((until - at) / DAY_MS));
  return `Deployed ${shortDate(at)} until ${shortDate(until)} (${days} days)`;
};

/** "12 Mar 24 → 4 Apr 24", with the certainty of each end where it is not a record. */
const rangeLabel = (bar: TimelineBar) => {
  const start = `${shortDate(bar.start)}${bar.startKind === 'actual' ? '' : bar.startKind === 'scheduled' ? ' (sched.)' : ' (est.)'}`;
  if (bar.endKind === 'open') return `${start} → no end yet`;
  const end = `${shortDate(bar.end)}${bar.endKind === 'actual' ? '' : bar.endKind === 'scheduled' ? ' (sched.)' : ' (est.)'}`;
  return `${start} → ${end}`;
};

/** A segment's width as a share of the bar it sits in, so it survives clipping at the edges. */
const segmentWidth = (bar: TimelineBar, a: number, b: number, pos: (t: number) => number) => {
  const left = pos(bar.start);
  const right = pos(bar.endKind === 'open' ? Infinity : bar.end);
  const span = right - left;
  if (span <= 0) return 0;
  return ((pos(b) - pos(a)) / span) * 100;
};

type EmptyMessageParams = { visible: number; hiddenByWindow: number; from: number; to: number };

/**
 * Why the timeline is empty, in the order the causes can occur.
 *
 * It used to always blame the date window, which on a view with no experiments at all
 * (Running, on a site with nothing running) quoted a window that did not exist:
 * "Nothing falls between Jul 30 and Jan 26" under All time.
 */
const emptyMessage = ({ visible, hiddenByWindow, from, to }: EmptyMessageParams): string => {
  if (visible === 0) return 'No experiments match the search or filter.';
  if (hiddenByWindow > 0) {
    return `Nothing that matches falls between ${shortDate(from)} and ${shortDate(to)}. ${hiddenByWindow} ${hiddenByWindow === 1 ? 'falls' : 'fall'} outside it: try a longer range, or All time.`;
  }
  return `None of the ${visible} experiment${visible === 1 ? '' : 's'} that match can be placed yet. Drafts appear once they are scheduled or queued in a pipeline.`;
};

const describe = (bar: TimelineBar) => {
  const start = `${bar.startKind === 'actual' ? 'started' : bar.startKind === 'scheduled' ? 'starts' : 'expected to start'} ${shortDate(bar.start)}`;
  switch (bar.endKind) {
    case 'actual':
      return `${start}, ended ${shortDate(bar.end)}`;
    case 'scheduled':
      return `${start}, scheduled to stop ${shortDate(bar.end)}`;
    case 'estimated':
      return `${start}, estimated end ${shortDate(bar.end)}`;
    case 'open':
      return `${start}, no end estimated yet`;
  }
};
